import { LitElement, css, html, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import classNames from "classnames";

import { styles as spotlightStyles } from "../../utils/spotlight";

import type {
  DetailListItem,
  MacroLiteraryDesignData,
  MacroLiteraryDesignGroup,
} from "../../types";

@customElement("bp-macro-literary-design")
export class MacroLiteraryDesign extends LitElement {
  static styles = [
    spotlightStyles,
    css`
      :host {
        /* Prevent text contents from being resized by slide. */
        --font-size-xs: 13px;
        --font-size-sm: 14px;
        --font-size-md: 16px;
        --font-size-lg: 18px;
        --font-size-xl: 20px;
      }

      .container {
        display: flex;
        flex-direction: row;
        gap: var(--size-1);
        padding: var(--size-1);
      }

      .container.vertical {
        width: var(--vertical-width);
        flex-direction: column;
      }

      .group {
        --border-color: var(--color-black);
        --indent: 0px;
        position: relative;
        padding: var(--size-1);
        border-radius: var(--radius-md);
        border: 2px solid var(--border-color);
        gap: var(--size-0-5);
        display: flex;
        flex-direction: column;
        margin-top: var(--indent);
        transition:
          color var(--spotlight-transition-duration),
          border var(--spotlight-transition-duration),
          background-color var(--spotlight-transition-duration);
      }

      .vertical .group {
        align-content: start;
        margin-top: unset;
        margin-inline-start: var(--indent);
      }

      .leaf {
        width: var(--size-10);
        min-height: calc(var(--size-25) - var(--indent));
      }

      .leaf.has-detail-list {
        min-width: var(--size-20);
        width: var(--size-20);
      }

      .leaf.has-title {
        min-width: var(--size-15);
        width: var(--size-15);
      }

      .leaf.has-detail-list.has-title {
        min-width: var(--size-20);
        width: var(--size-20);
      }

      .vertical .leaf {
        min-height: unset;
        width: unset;
      }

      .vertical .leaf.has-detail-list.has-title {
        min-height: unset;
        width: unset;
      }

      .title {
        font-weight: var(--font-weight-semibold);
        hyphens: auto;
        color: var(--color-black);
        transition: color var(--spotlight-transition-duration);
      }

      .detail-list {
        font-size: var(--font-size-sm);
        line-height: var(--line-height-normal);
        hyphens: auto;
        color: var(--color-black);
        transition: color var(--spotlight-transition-duration);
      }

      .detail-list ol {
        margin: var(--size-1) 0 var(--size-1) -26px;
        list-style: disc;
      }

      .detail-list.custom ol {
        margin-left: -32px;
        list-style: none;
      }

      .detail-list.custom li {
        display: flex;
      }

      .detail-list.custom li::before {
        padding-right: 5px;
        white-space: nowrap;
      }

      .custom li::before {
        content: attr(value) " -";
      }

      .custom li[value=""]::before {
        content: "•";
        font-size: 22px;
        line-height: 23px;
        margin-top: -2px;
        margin-right: 4px;
        margin-left: -1px;
      }

      .detail-list.custom li[data-indent="1"] {
        padding-left: var(--size-2);
      }

      .detail-list.custom li[data-indent="2"] {
        padding-left: var(--size-4);
      }

      .detail-list.custom li[data-indent="3"] {
        padding-left: var(--size-6);
      }

      .li-ref {
        white-space: nowrap;
        margin-right: 4px;
        font-weight: var(--font-weight-semibold);
      }

      .li-detail {
        word-wrap: break-word;
        min-width: var(--size-1);
      }

      .group[data-indent="1"] {
        --indent: var(--size-3);
      }

      .group[data-indent="2"] {
        --indent: var(--size-6);
      }

      .group[data-indent="3"] {
        --indent: var(--size-9);
      }

      .group[data-indent="4"] {
        --indent: var(--size-12);
      }

      .group[data-indent="5"] {
        --indent: var(--size-15);
      }

      .group[data-indent="6"] {
        --indent: calc(var(--size-15) + var(--size-3));
      }

      .group[data-indent="7"] {
        --indent: calc(var(--size-15) + var(--size-6));
      }

      .subgroups {
        padding-top: var(--size-0-5);
        display: flex;
        flex-direction: row;
        gap: var(--size-1);
        flex-grow: 1;
      }

      .vertical .subgroups {
        flex-direction: column;
      }

      .fill-background {
        background-color: color-mix(
          in srgb,
          var(--border-color),
          transparent 85%
        );
      }

      .tag {
        position: absolute;
        bottom: calc(100% + var(--size-1));
        inset-inline-end: 0;
        inset-inline-start: 0;
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .vertical .tag {
        inset-inline-end: calc(100% + var(--size-1));
        inset-inline-start: unset;
        top: 0;
        bottom: 0;
        flex-direction: column;
      }

      .group[data-mark-color="red"][data-mark-variant="highlight-light"] {
        --border-color: var(--color-red-10);
      }

      .group[data-mark-color="orange"][data-mark-variant="highlight-light"] {
        --border-color: var(--color-orange-10);
      }

      .group[data-mark-color="green"][data-mark-variant="highlight-light"] {
        --border-color: var(--color-green-10);
      }

      .group[data-mark-color="turquoise"][data-mark-variant="highlight-light"] {
        --border-color: var(--color-turquoise-10);
      }

      .group[data-mark-color="cyan"][data-mark-variant="highlight-light"] {
        --border-color: var(--color-cyan-10);
      }

      .group[data-mark-color="blue"][data-mark-variant="highlight-light"] {
        --border-color: var(--color-blue-10);
      }

      .group[data-mark-color="violet"][data-mark-variant="highlight-light"] {
        --border-color: var(--color-violet-10);
      }

      .group[data-mark-color="purple"][data-mark-variant="highlight-light"] {
        --border-color: var(--color-purple-10);
      }

      .group[data-mark-color="tan"][data-mark-variant="highlight-light"] {
        --border-color: var(--color-tan-10);
      }

      .group[data-mark-color="black"][data-mark-variant="highlight-light"] {
        --border-color: var(--color-neutral-10);
      }

      .group[data-mark-color="red"][data-mark-variant="highlight"] {
        --border-color: var(--color-red-50);
      }

      .group[data-mark-color="orange"][data-mark-variant="highlight"] {
        --border-color: var(--color-orange-50);
      }

      .group[data-mark-color="green"][data-mark-variant="highlight"] {
        --border-color: var(--color-green-50);
      }

      .group[data-mark-color="turquoise"][data-mark-variant="highlight"] {
        --border-color: var(--color-turquoise-50);
      }

      .group[data-mark-color="cyan"][data-mark-variant="highlight"] {
        --border-color: var(--color-cyan-50);
      }

      .group[data-mark-color="blue"][data-mark-variant="highlight"] {
        --border-color: var(--color-blue-50);
      }

      .group[data-mark-color="violet"][data-mark-variant="highlight"] {
        --border-color: var(--color-violet-50);
      }

      .group[data-mark-color="purple"][data-mark-variant="highlight"] {
        --border-color: var(--color-purple-50);
      }

      .group[data-mark-color="tan"][data-mark-variant="highlight"] {
        --border-color: var(--color-tan-50);
      }

      .group[data-mark-color="black"][data-mark-variant="highlight"] {
        --border-color: var(--color-neutral-50);
      }

      .group[data-mark-color="red"][data-mark-variant="highlight-dark"] {
        --border-color: var(--color-red-70);
      }

      .group[data-mark-color="orange"][data-mark-variant="highlight-dark"] {
        --border-color: var(--color-orange-70);
      }

      .group[data-mark-color="green"][data-mark-variant="highlight-dark"] {
        --border-color: var(--color-green-70);
      }

      .group[data-mark-color="turquoise"][data-mark-variant="highlight-dark"] {
        --border-color: var(--color-turquoise-70);
      }

      .group[data-mark-color="cyan"][data-mark-variant="highlight-dark"] {
        --border-color: var(--color-cyan-70);
      }

      .group[data-mark-color="blue"][data-mark-variant="highlight-dark"] {
        --border-color: var(--color-blue-70);
      }

      .group[data-mark-color="violet"][data-mark-variant="highlight-dark"] {
        --border-color: var(--color-violet-70);
      }

      .group[data-mark-color="purple"][data-mark-variant="highlight-dark"] {
        --border-color: var(--color-purple-70);
      }

      .group[data-mark-color="tan"][data-mark-variant="highlight-dark"] {
        --border-color: var(--color-tan-70);
      }

      .group[data-mark-color="black"][data-mark-variant="highlight-dark"] {
        --border-color: var(--color-neutral-70);
      }
    `,
  ];

  @property({ type: Object, attribute: "design-data" })
  data?: MacroLiteraryDesignData;

  renderGroup(
    data: MacroLiteraryDesignGroup,
    direction: string,
    id: string,
    depth = 0,
  ): TemplateResult {
    const className = classNames("group", {
      leaf: !data.subgroups?.length,
      "has-detail-list": data.detailList?.length,
      "has-title": data.title,
      "fill-background": data.fillBackground,
    });
    const reference = data.reference
      ? html`<div class="reference">
          <bp-mark color="${data.markColor}" variant="${data.markVariant}">
            ${data.reference}
          </bp-mark>
        </div>`
      : "";
    const title = data.title
      ? html`<div class="title">${unsafeHTML(data.title)}</div>`
      : "";
    const detailListClassName = classNames("detail-list", data.detailListType);
    const detailList = data.detailList?.length
      ? html`<div class="${detailListClassName}">
          <ol>
            ${data.detailList.map((item) => {
              const listItem = item as DetailListItem;
              const listRef =
                data.detailListType == "custom" && listItem.ref
                  ? html`<span class="li-ref">${listItem.ref}</span> `
                  : "";
              if (!listItem) return null;
              return html`<li
                data-indent="${listItem.indent ?? 0}"
                value="${listItem.tag ?? ""}"
              >
                ${direction == "vertical" ? listRef : ""}
                <span class="li-detail">
                  ${direction == "horizontal" ? listRef : ""}
                  ${unsafeHTML(listItem.detail ?? item)}</span
                >
              </li>`;
            })}
          </ol>
        </div>`
      : "";
    const subgroups = data.subgroups?.length
      ? html`<div class="subgroups">
          ${data.subgroups.map((subgroup, index) =>
            this.renderGroup(
              subgroup,
              direction,
              `${id}-${index + 1}`,
              depth + 1,
            ),
          )}
        </div>`
      : "";
    const tag = data.tag ? html`<div class="tag">${data.tag}</div>` : "";
    return html`<div
      class="${className}"
      data-mark-color="${data.markColor}"
      data-mark-variant="${data.markVariant}"
      data-id="group-${id}"
      data-indent="${data.indent}"
      data-depth=${depth}
    >
      ${reference} ${title} ${detailList} ${subgroups} ${tag}
    </div>`;
  }

  render() {
    if (!this.data?.groups?.length) return;
    const className = classNames("container", this.data.direction);
    return html`<div class="${className}">
      ${this.data.groups.map((group, index) =>
        this.renderGroup(group, this.data?.direction ?? "", `${index + 1}`),
      )}
    </div>`;
  }
}
